import { apiSlice } from '@/features/api/apiSlice';

export type CommentRow = {
  id: string;
  blockId: string;
  authorId: string;
  author: { id: string; name: string | null; email: string; avatarUrl: string | null } | null;
  body: string;
  parentId: string | null;
  mentions: string[];
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ApprovalRow = {
  id: string;
  versionId: string;
  requestedBy: string;
  approverId: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  decidedAt: string | null;
  note: string | null;
  createdAt: string;
};

export const collabApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    listComments: b.query<CommentRow[], string>({
      query: (versionId) => `/versions/${versionId}/comments`,
      providesTags: (_r, _e, vid) => [{ type: 'Template', id: `COMMENTS_${vid}` }],
    }),
    createComment: b.mutation<
      CommentRow,
      { versionId: string; blockId: string; body: string; parentId?: string; mentions?: string[] }
    >({
      query: ({ versionId, ...body }) => ({
        url: `/versions/${versionId}/comments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { versionId }) => [{ type: 'Template', id: `COMMENTS_${versionId}` }],
    }),
    resolveComment: b.mutation<CommentRow, { versionId: string; id: string }>({
      query: ({ versionId, id }) => ({ url: `/versions/${versionId}/comments/${id}/resolve`, method: 'POST' }),
      invalidatesTags: (_r, _e, { versionId }) => [{ type: 'Template', id: `COMMENTS_${versionId}` }],
    }),
    reopenComment: b.mutation<CommentRow, { versionId: string; id: string }>({
      query: ({ versionId, id }) => ({ url: `/versions/${versionId}/comments/${id}/reopen`, method: 'POST' }),
      invalidatesTags: (_r, _e, { versionId }) => [{ type: 'Template', id: `COMMENTS_${versionId}` }],
    }),
    deleteComment: b.mutation<void, { versionId: string; id: string }>({
      query: ({ versionId, id }) => ({ url: `/versions/${versionId}/comments/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { versionId }) => [{ type: 'Template', id: `COMMENTS_${versionId}` }],
    }),

    listApprovals: b.query<ApprovalRow[], string>({
      query: (versionId) => `/versions/${versionId}/approvals`,
      providesTags: (_r, _e, vid) => [{ type: 'Template', id: `APPROVALS_${vid}` }],
    }),
    requestApproval: b.mutation<ApprovalRow, { versionId: string; approverId?: string; note?: string }>({
      query: ({ versionId, ...body }) => ({ url: `/versions/${versionId}/approvals`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { versionId }) => [{ type: 'Template', id: `APPROVALS_${versionId}` }],
    }),
    decideApproval: b.mutation<
      ApprovalRow,
      { versionId: string; approvalId: string; decision: 'approved' | 'rejected'; note?: string }
    >({
      query: ({ versionId, approvalId, ...body }) => ({
        url: `/versions/${versionId}/approvals/${approvalId}/decide`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { versionId }) => [{ type: 'Template', id: `APPROVALS_${versionId}` }],
    }),
  }),
});

export const {
  useListCommentsQuery,
  useCreateCommentMutation,
  useResolveCommentMutation,
  useReopenCommentMutation,
  useDeleteCommentMutation,
  useListApprovalsQuery,
  useRequestApprovalMutation,
  useDecideApprovalMutation,
} = collabApi;
